import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Vote, Check, ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { LanguageSelector } from '../components/LanguageSelector';
import { Player } from '../types';

interface VotingPageProps {
  players: Player[];
  currentPlayer: Player;
  onBack: () => void;
  onVote: (targetId: string) => void;
}

export const VotingPage: React.FC<VotingPageProps> = ({
  players,
  currentPlayer,
  onBack,
  onVote,
}) => {
  const { t } = useTranslation();
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
  const [hasVoted, setHasVoted] = useState(false);

  const handleVote = (playerId: string) => {
    if (hasVoted) return;
    
    setSelectedPlayer(playerId);
    setHasVoted(true);
    onVote(playerId);
  };

  const availablePlayers = players.filter(p => p.id !== currentPlayer.id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%239C92AC%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 z-10">
        <Button
          variant="secondary"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          {t('common.back')}
        </Button>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display">
            {t('voting.title')}
          </h1>
          <p className="text-xl text-white/80">
            {t('voting.instructions')}
          </p>
        </div>

        {/* Voting Status */}
        <Card className="mb-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Vote className="w-6 h-6 text-white" />
            <h2 className="text-2xl font-bold text-white">
              {hasVoted ? 'Voto registrado' : 'Selecciona tu voto'}
            </h2>
          </div>
          
          {hasVoted && selectedPlayer && (
            <div className="p-4 bg-success-500/20 border border-success-400 rounded-xl">
              <p className="text-success-400 font-medium">
                {t('voting.voted', { 
                  name: players.find(p => p.id === selectedPlayer)?.name 
                })}
              </p>
            </div>
          )}
        </Card>

        {/* Players to Vote */}
        <Card>
          <h3 className="text-xl font-semibold text-white mb-6 text-center">
            Selecciona al impostor
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availablePlayers.map((player) => (
              <button
                key={player.id}
                onClick={() => handleVote(player.id)}
                disabled={hasVoted}
                className={`p-6 rounded-xl border-2 transition-all duration-200 ${
                  selectedPlayer === player.id
                    ? 'bg-accent-500/20 border-accent-400 scale-105'
                    : hasVoted
                    ? 'bg-white/5 border-white/20 opacity-50 cursor-not-allowed'
                    : 'bg-white/5 border-white/20 hover:bg-white/10 hover:scale-105'
                }`}
              >
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 ${
                    selectedPlayer === player.id
                      ? 'bg-accent-500'
                      : 'bg-white/20'
                  }`}>
                    {selectedPlayer === player.id ? (
                      <Check className="w-8 h-8 text-white" />
                    ) : (
                      <span className="text-2xl font-bold text-white">
                        {player.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-1">
                    {player.name}
                  </h4>
                  <p className="text-white/60 text-sm">
                    {selectedPlayer === player.id ? 'Seleccionado' : 'Hacer clic para votar'}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Waiting Message */}
        {hasVoted && (
          <Card className="mt-6 text-center">
            <div className="flex items-center justify-center gap-3">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <p className="text-white/80">
                {t('voting.waitingForVotes')}
              </p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
